<template>
  <div class="d-flex flex-column gap-3">
    
    <div v-if="filteredOptions.length > 0" class="d-flex flex-column gap-2">
      <!-- Search & Select All -->
      <div class="flex gap-2">
        <el-checkbox
          v-model="allSelected"
          :indeterminate="someSelected"
        >
          {{ $t('views.default.toggle.selectAll') }}
        </el-checkbox>
        <el-input
          :placeholder="placeholder"
          v-model="searchTerm"
        />
      </div>

      <!-- Grid of items -->
      <div class="item-list">
        <MeshDesignSelector
          :selectedEngraving="selected"
          :meshEngravings="filteredOptions"
          :multiple="multiple"
          @mesh-design-selected="onSelect"
        />
      </div>
  
      <!-- Counter -->
      <div class="fs-6 text-info">{{ countMessage }}</div>

    </div>

  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-facing-decorator';
import MeshDesignSelector from '@/components/configurator/MeshDesignsSelector.vue';
import { SupportedValues, SupportedValuesTextures } from '@/types/api/Utility/SupportedValues';

@Component({
  components: { MeshDesignSelector },
  emits: ['update:selected']
})
export default class DesignList extends Vue {
  @Prop({ required: true }) readonly selected!: number | number[];
  @Prop({ required: true }) readonly options!: Array<SupportedValues | SupportedValuesTextures>;
  @Prop({ default: '' }) readonly placeholder!: string;
  @Prop({ default: true }) readonly multiple!: boolean;
  @Prop({ required: true }) readonly dataType!: 'engraving' | 'texture';

  searchTerm = '';

  get filteredOptions() {
    if (!this.searchTerm) return this.options;
    const kw = this.searchTerm.toLowerCase();
    return this.options.filter(opt => {
      const raw = opt.thumbnail?.name || '-';
      const label = (this.$t(`views.designs.${raw}`) as string) || raw;
      return label.toLowerCase().includes(kw);
    });
  }

  get messageKey() {
    return this.dataType === 'engraving'
      ? 'components.create-template.engravingDesignSelected'
      : 'components.create-template.textureDesignSelected';
  }

  get countMessage() {
    const cnt = Array.isArray(this.selected)
      ? this.selected.length
      : this.selected
      ? 1
      : 0;
    return `${this.$t(this.messageKey)} (${cnt} ${this.$t('components.create-template.items')})`;
  }

  get allOptionIds(): number[] {
    return this.options
      .map(opt =>
        this.dataType === 'engraving'
          ? (opt as SupportedValues).fileId
          : (opt as SupportedValuesTextures).fileId
      )
      .filter((id): id is number => id != null);
  }

  get allSelected(): boolean {
    const sel = Array.isArray(this.selected) ? this.selected : [this.selected];
    return this.allOptionIds.length > 0 && this.allOptionIds.every(id => sel.includes(id));
  }
  set allSelected(val: boolean) {
    const newItems = val ? this.options : [];
    if (this.multiple) {
      this.$emit('update:selected', newItems);
    } else {
      this.$emit('update:selected', newItems[0] || null);
    }
  }

  get someSelected(): boolean {
    const selCount = Array.isArray(this.selected)
      ? this.selected.length
      : this.selected
      ? 1
      : 0;
    return selCount > 0 && selCount < this.allOptionIds.length;
  }

  onSelect(value: SupportedValues | SupportedValues[] ) {
    this.$emit('update:selected', value);
  }
}
</script>

<style scoped>
.max-h-custom {
  max-height: 200px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(5.7rem, 1fr));
  gap: 0.8rem;
  justify-content: center;
  align-items: center;
}
.text-info {
  font-size: 16px;
  color: #ee3a3a !important;
}

.item-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  max-height: 200px;
  overflow-y: auto;
  padding: .5rem .25rem;
}

.item-list > div {
  flex: 0 0 5.7rem;
}
</style>
